import React, { useRef } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { Field, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomButton from '../../../components/@extended/CustomButton';
import { VisuallyHiddenInput } from '../../../components/common/DocumentUpload';
import Phone from '../../../components/common/Phone';
import { formatCountryOption, getCountryByCode } from '../../../constants/iso3166Countries';
import {
  formatUsStateOption,
  getUsStateByCode,
  usStates,
} from '../../../constants/usStates';
import { useAddFinanceEntityMutation, useUploadAccountCsvMutation } from '../../../store/slices/apiSlice';
import { openMessage } from '../../../store/slices/showMessageSlice';
import { sanitizeApiErrorMessage } from '../../../util/util';
import {
  bankCountries,
  ENTITY_TYPES,
  entityOnboardValidationSchema,
  getEntityTypeDefaults,
  getInitialValues,
  mapFormValuesToPayload,
} from './entityOnboardValidation';

const allowedFileTypes =
  '.csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const PersonFields = ({
  prefix,
  title,
  touched,
  errors,
  handleChange,
  values,
  setFieldValue,
  submitCount,
}) => {
  const person = values[prefix] || {};
  const personTouched = touched[prefix] || {};
  const personErrors = errors[prefix] || {};

  return (
    <>
      <Grid item xs={12}>
        <Typography fontWeight={600}>{title}</Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          name={`${prefix}.firstName`}
          label="First Name"
          value={person.firstName || ''}
          onChange={handleChange}
          error={personTouched.firstName && Boolean(personErrors.firstName)}
          helperText={personTouched.firstName && personErrors.firstName}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          name={`${prefix}.lastName`}
          label="Last Name"
          value={person.lastName || ''}
          onChange={handleChange}
          error={personTouched.lastName && Boolean(personErrors.lastName)}
          helperText={personTouched.lastName && personErrors.lastName}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          type="email"
          name={`${prefix}.email`}
          label="Email"
          value={person.email || ''}
          onChange={handleChange}
          error={personTouched.email && Boolean(personErrors.email)}
          helperText={personTouched.email && personErrors.email}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Field
          name={`${prefix}.phone`}
          component={Phone}
          label="Contact Number"
          countries={['US']}
          defaultCountry="US"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date of Birth"
            disableFuture
            value={person.dob || null}
            onChange={(newValue) => setFieldValue(`${prefix}.dob`, newValue)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                error: (personTouched.dob || submitCount > 0) && Boolean(personErrors.dob),
                helperText: (personTouched.dob || submitCount > 0) && personErrors.dob,
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          name={`${prefix}.id_number`}
          label="Social Security Number"
          value={person.id_number || ''}
          onChange={handleChange}
          error={personTouched.id_number && Boolean(personErrors.id_number)}
          helperText={personTouched.id_number && personErrors.id_number}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2" fontWeight={500}>
          Address
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          name={`${prefix}.address.line1`}
          label="Address Line 1"
          value={person.address?.line1 || ''}
          onChange={handleChange}
          error={personTouched.address?.line1 && Boolean(personErrors.address?.line1)}
          helperText={personTouched.address?.line1 && personErrors.address?.line1}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          name={`${prefix}.address.line2`}
          label="Address Line 2"
          value={person.address?.line2 || ''}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          name={`${prefix}.address.city`}
          label="City"
          value={person.address?.city || ''}
          onChange={handleChange}
          error={personTouched.address?.city && Boolean(personErrors.address?.city)}
          helperText={personTouched.address?.city && personErrors.address?.city}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl
          fullWidth
          size="small"
          error={personTouched.address?.state && Boolean(personErrors.address?.state)}
        >
          <InputLabel id={`${prefix}-state-label`}>State</InputLabel>
          <Select
            labelId={`${prefix}-state-label`}
            name={`${prefix}.address.state`}
            value={person.address?.state || ''}
            label="State"
            onChange={handleChange}
            renderValue={(value) => {
              const state = getUsStateByCode(value);
              return state ? formatUsStateOption(state) : value;
            }}
          >
            <MenuItem disabled value="">
              <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select state</em>
            </MenuItem>
            {usStates.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {formatUsStateOption(option)}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{personTouched.address?.state && personErrors.address?.state}</FormHelperText>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          name={`${prefix}.address.postalCode`}
          label="ZIP Code"
          value={person.address?.postalCode || ''}
          onChange={handleChange}
          error={personTouched.address?.postalCode && Boolean(personErrors.address?.postalCode)}
          helperText={personTouched.address?.postalCode && personErrors.address?.postalCode}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          disabled
          label="Country"
          value="United States"
        />
      </Grid>
    </>
  );
};

const CompanyAddressFields = ({ touched, errors, handleChange, values }) => (
  <>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        size="small"
        name="company.name"
        label="Business Name"
        value={values.company?.name || ''}
        onChange={handleChange}
        error={touched.company?.name && Boolean(errors.company?.name)}
        helperText={touched.company?.name && errors.company?.name}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        size="small"
        name="company.taxId"
        label="Tax ID (EIN)"
        value={values.company?.taxId || ''}
        onChange={handleChange}
        error={touched.company?.taxId && Boolean(errors.company?.taxId)}
        helperText={touched.company?.taxId && errors.company?.taxId}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        size="small"
        name="company.address.line1"
        label="Business Address Line 1"
        value={values.company?.address?.line1 || ''}
        onChange={handleChange}
        error={touched.company?.address?.line1 && Boolean(errors.company?.address?.line1)}
        helperText={touched.company?.address?.line1 && errors.company?.address?.line1}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        size="small"
        name="company.address.line2"
        label="Business Address Line 2"
        value={values.company?.address?.line2 || ''}
        onChange={handleChange}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        size="small"
        name="company.address.city"
        label="City"
        value={values.company?.address?.city || ''}
        onChange={handleChange}
        error={touched.company?.address?.city && Boolean(errors.company?.address?.city)}
        helperText={touched.company?.address?.city && errors.company?.address?.city}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <FormControl
        fullWidth
        size="small"
        error={touched.company?.address?.state && Boolean(errors.company?.address?.state)}
      >
        <InputLabel id="company-state-label">State</InputLabel>
        <Select
          labelId="company-state-label"
          name="company.address.state"
          value={values.company?.address?.state || ''}
          label="State"
          onChange={handleChange}
          renderValue={(value) => {
            const state = getUsStateByCode(value);
            return state ? formatUsStateOption(state) : value;
          }}
        >
          {usStates.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {formatUsStateOption(option)}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{touched.company?.address?.state && errors.company?.address?.state}</FormHelperText>
      </FormControl>
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        size="small"
        name="company.address.postalCode"
        label="ZIP Code"
        value={values.company?.address?.postalCode || ''}
        onChange={handleChange}
        error={touched.company?.address?.postalCode && Boolean(errors.company?.address?.postalCode)}
        helperText={touched.company?.address?.postalCode && errors.company?.address?.postalCode}
      />
    </Grid>
  </>
);

const FinanceEntityOnboardForm = ({ open, onClose, onEntityCreated }) => {
  const dispatch = useDispatch();
  const actor = useSelector((state) => state?.userDetails?.loggedInUser?.email);
  const fileInputRef = useRef(null);
  const [addFinanceEntity, { isLoading }] = useAddFinanceEntityMutation();
  const [uploadAccountCsv, { isLoading: isUploading }] = useUploadAccountCsvMutation();

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files?.length) return;

    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      const result = await uploadAccountCsv({ body: formData, actor }).unwrap();
      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: 'File uploaded successfully! Started Processing...',
        })
      );
      onEntityCreated?.(result);
    } catch (error) {
      const errorMessage = error?.data?.message?.includes('Missing required columns')
        ? 'Please upload a valid file with required columns.'
        : error?.data?.message || 'Document upload failed. Please try again.';

      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: errorMessage,
        })
      );
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.blur();
      }
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = mapFormValuesToPayload(values);
      const res = await addFinanceEntity({ body: payload, actor }).unwrap();

      const entityLabel =
        values.entityType === 'CUSTOM' ? values.customEntityType.trim() : values.entityType;
      const successMessage = `Entity (${entityLabel}) created. Complete Stripe verification via the onboarding email if required.`;

      dispatch(openMessage({ message: successMessage, messageSeverity: 'success' }));
      resetForm({ values: getInitialValues() });
      onEntityCreated?.(res);
      onClose();
    } catch (error) {
      const apiMessage = error?.data?.message;
      const messageText = Array.isArray(apiMessage) ? apiMessage.join(' ') : apiMessage;
      if (messageText?.toLowerCase().includes('already exists')) {
        dispatch(
          openMessage({
            message: 'An entity with this email already exists.',
            messageSeverity: 'error',
          })
        );
      } else {
        dispatch(
          openMessage({
            message: sanitizeApiErrorMessage(error) || 'Unable to create entity. Please try again.',
            messageSeverity: 'error',
          })
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" component="span">
            Onboard Account
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Upload file with CSV/Excel format.">
              <CustomButton
                variant="outlined"
                size="small"
                startIcon={
                  isUploading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CloudUploadOutlinedIcon fontSize="small" />
                  )
                }
                component="label"
                disabled={isUploading}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Upload Accounts
                <VisuallyHiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept={allowedFileTypes}
                  onChange={handleFileUpload}
                />
              </CustomButton>
            </Tooltip>
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={entityOnboardValidationSchema}
          onSubmit={handleSubmit}
          validateOnChange
          validateOnBlur
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            isSubmitting,
            submitCount,
            isValid,
          }) => (
            <Form>
              {submitCount > 0 && !isValid && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Please fix the highlighted errors before submitting.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography fontWeight={600}>Entity Configuration</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" error={touched.entityType && Boolean(errors.entityType)}>
                    <InputLabel id="entityType-label">Entity Type</InputLabel>
                    <Select
                      labelId="entityType-label"
                      name="entityType"
                      value={values.entityType}
                      label="Entity Type"
                      onChange={(e) => {
                        const newType = e.target.value;
                        handleChange(e);
                        const defaults = getEntityTypeDefaults(newType);
                        if (defaults.businessType) {
                          setFieldValue('businessType', defaults.businessType);
                        }
                        if (defaults.accountHolderType) {
                          setFieldValue('bankDetails.accountHolderType', defaults.accountHolderType);
                        }
                      }}
                    >
                      {ENTITY_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type === 'CUSTOM' ? 'Custom' : type.replace('_', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{touched.entityType && errors.entityType}</FormHelperText>
                  </FormControl>
                </Grid>
                {values.entityType === 'CUSTOM' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      name="customEntityType"
                      label="Custom Entity Type"
                      value={values.customEntityType}
                      onChange={handleChange}
                      error={touched.customEntityType && Boolean(errors.customEntityType)}
                      helperText={touched.customEntityType && errors.customEntityType}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" error={touched.businessType && Boolean(errors.businessType)}>
                    <InputLabel id="businessType-label">Business Type</InputLabel>
                    <Select
                      labelId="businessType-label"
                      name="businessType"
                      value={values.businessType}
                      label="Business Type"
                      onChange={handleChange}
                    >
                      <MenuItem value="individual">Individual</MenuItem>
                      <MenuItem value="company">Company</MenuItem>
                    </Select>
                    <FormHelperText>{touched.businessType && errors.businessType}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="externalMasterId"
                    label="External Master ID"
                    value={values.externalMasterId}
                    onChange={handleChange}
                    error={touched.externalMasterId && Boolean(errors.externalMasterId)}
                    helperText={touched.externalMasterId && errors.externalMasterId}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {values.businessType === 'company' && (
                  <>
                    <Grid item xs={12}>
                      <Typography fontWeight={600}>Business Details</Typography>
                    </Grid>
                    <CompanyAddressFields
                      touched={touched}
                      errors={errors}
                      handleChange={handleChange}
                      values={values}
                    />
                  </>
                )}

                {values.businessType === 'individual' ? (
                  <PersonFields
                    prefix="individual"
                    title="Individual Details"
                    touched={touched}
                    errors={errors}
                    handleChange={handleChange}
                    values={values}
                    setFieldValue={setFieldValue}
                    submitCount={submitCount}
                  />
                ) : (
                  <PersonFields
                    prefix="representative"
                    title="Representative Details"
                    touched={touched}
                    errors={errors}
                    handleChange={handleChange}
                    values={values}
                    setFieldValue={setFieldValue}
                    submitCount={submitCount}
                  />
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography fontWeight={600}>Bank Details</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="bankDetails.accountHolderName"
                    label="Account Holder Name"
                    value={values.bankDetails.accountHolderName}
                    onChange={handleChange}
                    error={
                      touched.bankDetails?.accountHolderName &&
                      Boolean(errors.bankDetails?.accountHolderName)
                    }
                    helperText={
                      touched.bankDetails?.accountHolderName && errors.bankDetails?.accountHolderName
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={
                      touched.bankDetails?.accountHolderType &&
                      Boolean(errors.bankDetails?.accountHolderType)
                    }
                  >
                    <InputLabel id="accountHolderType-label">Account Holder Type</InputLabel>
                    <Select
                      labelId="accountHolderType-label"
                      name="bankDetails.accountHolderType"
                      value={values.bankDetails.accountHolderType}
                      label="Account Holder Type"
                      onChange={handleChange}
                    >
                      <MenuItem value="company">Company</MenuItem>
                      <MenuItem value="individual">Individual</MenuItem>
                    </Select>
                    <FormHelperText>
                      {touched.bankDetails?.accountHolderType && errors.bankDetails?.accountHolderType}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="bankDetails.accountNumber"
                    label="Account Number"
                    value={values.bankDetails.accountNumber}
                    onChange={handleChange}
                    error={
                      touched.bankDetails?.accountNumber && Boolean(errors.bankDetails?.accountNumber)
                    }
                    helperText={touched.bankDetails?.accountNumber && errors.bankDetails?.accountNumber}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="bankDetails.confirmAccountNumber"
                    label="Confirm Account Number"
                    value={values.bankDetails.confirmAccountNumber}
                    onChange={handleChange}
                    error={
                      touched.bankDetails?.confirmAccountNumber &&
                      Boolean(errors.bankDetails?.confirmAccountNumber)
                    }
                    helperText={
                      touched.bankDetails?.confirmAccountNumber &&
                      errors.bankDetails?.confirmAccountNumber
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="bankDetails.routingNumber"
                    label="Routing Number"
                    value={values.bankDetails.routingNumber}
                    onChange={handleChange}
                    error={
                      touched.bankDetails?.routingNumber && Boolean(errors.bankDetails?.routingNumber)
                    }
                    helperText={touched.bankDetails?.routingNumber && errors.bankDetails?.routingNumber}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={touched.bankDetails?.country && Boolean(errors.bankDetails?.country)}
                  >
                    <InputLabel id="bankCountry-label">Bank Country</InputLabel>
                    <Select
                      labelId="bankCountry-label"
                      name="bankDetails.country"
                      value={values.bankDetails.country}
                      label="Bank Country"
                      onChange={(event) => {
                        const selected = bankCountries.find((c) => c.code === event.target.value);
                        setFieldValue('bankDetails.country', event.target.value);
                        setFieldValue('bankDetails.currency', selected?.currency || '');
                      }}
                      renderValue={(value) => {
                        const country = getCountryByCode(value, bankCountries);
                        return country ? formatCountryOption(country) : value;
                      }}
                    >
                      {bankCountries.map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                          {formatCountryOption(option)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{touched.bankDetails?.country && errors.bankDetails?.country}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled
                    name="bankDetails.currency"
                    label="Currency"
                    value={values.bankDetails.currency}
                  />
                </Grid>

                <Grid item xs={12} display="flex" justifyContent="center" gap={2} pt={2} pb={1}>
                  <CustomButton variant="outlined" onClick={onClose} disabled={isSubmitting || isLoading}>
                    Cancel
                  </CustomButton>
                  <CustomButton type="submit" variant="contained" disabled={isSubmitting || isLoading}>
                    {isSubmitting || isLoading ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      'Create Entity'
                    )}
                  </CustomButton>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default FinanceEntityOnboardForm;
